import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Modal as AntModal } from 'antd';
import produce from 'immer';
import { TableMainText } from 'styles';

import { DEFAULT_PAGE_SIZE } from 'appConstants';
import { TableActions, TableTimestamp } from 'components';
import CustomIcon from 'components/Icons/Icon';

import BDOSettingsCardComponents from '../BDOSettingsCardComponents';
import { clearBDOSettings, deleteBDOSetting, getBDOSettings } from '../action';
import { ICreditCardReducerState } from '../types';
import AddEditMemFeeWaiverModal from './AddEditMemFeeWaiverModal';

const { confirm } = AntModal;

const MemFeeWaiverLists = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedMemFeeWaiver, setSelectedMemFeeWaiver] = useState<number | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bdoSettings, loading } = useSelector((state: any) => state.bdoSettings);

  const paramsReducer = (state: ICreditCardReducerState, action): ICreditCardReducerState =>
    produce(state, draft => {
      switch (action.type) {
        case 'SEARCH':
          draft.q = action.value;
          draft.page = 1;
          break;
        case 'PAGINATION':
          draft.page = action.current;
          if (action.size) {
            draft.size = action.size;
          }
          break;
        default:
          break;
      }
    });

  const [params, paramsDispatch] = useReducer(paramsReducer, {
    q: null,
    size: DEFAULT_PAGE_SIZE,
    page: 1,
    type: 'membership_fee'
  });

  useEffect(() => {
    dispatch(getBDOSettings(params));
  }, [params, dispatch]);

  const onCloseModal = () => {
    dispatch(clearBDOSettings());
    setIsEdit(false);
    setModalOpen(false);
    setSelectedMemFeeWaiver(null);
  };

  const columns = [
    {
      title: '',
      key: 'sort',
      width: '3%',
      render: () => <CustomIcon iconName="sort" />
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <>
          <Link to={`/p/credit_card_redemption_settings/mem-fee-waiver/show/${record.id}`}>
            <TableMainText>{record.name}</TableMainText>
          </Link>
        </>
      )
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      render: (_, record) => (record.cost > 0 ? record.cost : '-')
    },
    {
      title: 'Supp. Card Cost',
      dataIndex: 'supplementary_cost',
      key: 'supplementary_cost',
      render: (_, record) => record.supplementary_cost ?? '-'
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (_, record) => <TableTimestamp timestamp={record['start_date']} />
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (_, record) => <TableTimestamp timestamp={record['end_date']} />
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <TableActions
          testId="mem-fee-waiver-action-menu"
          actions={[
            {
              title: 'View Details',
              onClick: () => {
                navigate(`/p/credit_card_redemption_settings/mem-fee-waiver/show/${record.id}`);
              }
            },
            {
              title: 'Edit',
              onClick: () => {
                setIsEdit(true);
                setSelectedMemFeeWaiver(record.id);
                setModalOpen(true);
              }
            },
            {
              title: 'Delete',
              onClick: () =>
                confirm({
                  title: 'Do you want to delete this item?',
                  onOk() {
                    dispatch(deleteBDOSetting(record.id));
                  }
                })
            }
          ]}
        />
      ),
      align: 'right' as 'right'
    }
  ];

  return (
    <>
      <BDOSettingsCardComponents
        cardTitle="Mem Fee Waiver"
        data={bdoSettings}
        columns={columns}
        mutate={() => dispatch(getBDOSettings(params))}
        params={params}
        paramsDispatch={paramsDispatch}
        loading={loading}
        onAddNewClick={() => {
          dispatch(clearBDOSettings());
          setIsEdit(false);
          setModalOpen(true);
          setSelectedMemFeeWaiver(null);
        }}
      />
      <AddEditMemFeeWaiverModal
        modalOpen={modalOpen}
        isEdit={isEdit}
        closeModal={onCloseModal}
        selectedMemFeeWaiver={selectedMemFeeWaiver}
      />
    </>
  );
};

export default MemFeeWaiverLists;
